import { useTranslation } from 'react-i18next';
import { useTrackNavigationClicked } from '../../utils/analytics';
import { isGenAiLogomakerAvailable } from '../../utils/featureAvailability';
import { logoProductsPricing } from '../../utils/pricing';
import { proLogoDesignUrl } from '../../utils/urls';
import type { Locale } from '../../i18n/config';

export type LogoProductKey = 'logomaker' | 'logo-essential' | 'logo-contest';

type LogoProductTile = {
    title: string;
    description: string;
    imageDefault: { src: string; altText: string };
    linkHref: string;
    ctaText: string;
    priceText?: string;
    ctaRef: (node: HTMLElement) => void;
};

type LogoProductTilesConfig = Record<LogoProductKey, LogoProductTile>;

export const useLogoProductTilesConfig = (
    trackingCategory: string,
    locale: Locale,
): LogoProductTilesConfig => {
    const { t } = useTranslation();

    const classicLogoProductTiles = {
        logomaker: {
            title: t('Vista Logos > Logo Product Tile > Logomaker.productTileHeader'),
            description: t('Vista Logos > Logo Product Tile > Logomaker.productTileSubHeader'),
            imageDefault: {
                src: t('Vista Logos > Logo Product Tile > Logomaker.productImage.src'),
                altText: '',
            },
            linkHref: t('Vista Logos > Logo Product Tile > Logomaker.url.contentfulUrl'),
            ctaText: t('Vista Logos > Logo Product Tile > Logomaker.urlText'),
            priceText: t('Vista Logos > Logo Product Tile > Logomaker > Price Text.value'),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                t('Vista Logos > Logo Product Tile > Logomaker.url.contentfulUrl'),
                'createYourFreeLogo',
            ),
        },
        'logo-essential': {
            title: t('Vista Logos > Logo Product Tile > Logo Essential.productTileHeader'),
            description: t('Vista Logos > Logo Product Tile > Logo Essential.productTileSubHeader'),
            imageDefault: {
                src: t('Vista Logos > Logo Product Tile > Logo Essential.productImage.src'),
                altText: '',
            },
            linkHref: proLogoDesignUrl(locale),
            ctaText: t('Vista Logos > Logo Product Tile > Logo Essential.urlText'),
            priceText: t('Vista Logos > Logo Product Tile > Logo Essential > Price Text.value', {
                logoEssentialPriceAmount: logoProductsPricing[locale]['logo-essential'],
            }),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                proLogoDesignUrl(locale),
                'seePackageDetails',
            ),
        },
        'logo-contest': {
            title: t('Vista Logos > Logo Product Tile > Logo Contest.productTileHeader'),
            description: t('Vista Logos > Logo Product Tile > Logo Contest.productTileSubHeader'),
            imageDefault: {
                src: t('Vista Logos > Logo Product Tile > Logo Contest.productImage.src'),
                altText: '',
            },
            linkHref: t('Vista Logos > Logo Product Tile > Logo Contest.url.contentfulUrl'),
            ctaText: t('Vista Logos > Logo Product Tile > Logo Contest.urlText'),
            priceText: t('Vista Logos > Logo Product Tile > Logo Contest > Price Text.value', {
                logoContestBronzePriceAmount: logoProductsPricing[locale]['logo-contest-bronze'],
            }),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                t('Vista Logos > Logo Product Tile > Logo Contest.url.contentfulUrl'),
                'seeLogoContestDetails',
            ),
        },
    };

    const genAiLogoProductTiles = {
        ...classicLogoProductTiles,
        logomaker: {
            ...classicLogoProductTiles.logomaker,
            title: "Create a free, AI logo you'll love",
            description:
                "Get a logo made just for you with our intuitive AI. Built for businesses like yours, it's easy to refine ideas - then get perfect, print-ready files.",
        },
        'logo-essential': {
            ...classicLogoProductTiles['logo-essential'],
            title: 'Work with a designer for a handcrafted logo',
            description:
                'Need help from a professional? Collaborate with a vetted designer of your choosing and get a completely handcrafted logo at an affordable price.',
        },
        'logo-contest': {
            ...classicLogoProductTiles['logo-contest'],
            title: 'Get dozens of ideas, choose your favorite logo',
            description:
                'Get multiple, handcrafted logo ideas created by our professional design community at 99designs - then choose the logo you love or get your money back.',
        },
    };

    return isGenAiLogomakerAvailable(locale) ? genAiLogoProductTiles : classicLogoProductTiles;
};
